// Pure helpers for the vendor-lookalike issue's decide flow (Baker
// 2026-07-13). "Is X a business, not a person?" used to dead-end at
// 'The recommended action ("Retag as a vendor") needs the full review
// screen'. The honest answers are now one click each in the rail:
//
//   retag-as-vendor — "It's a business that serves US": flips
//     contact_type='vendor' via the already-wired decide verb (audited;
//     reversible via the contact's History tab).
//   keep-current — "It's a business we SERVE" (Grant 2026-08-10): a
//     client entity — one we lend to or serve — which is neither a
//     vendor nor a person. Keeps the contact out of the vendor bucket
//     and marks its household as the business it is.
//   dismiss — "It's a person": resolves as not-an-issue; a dismissed
//     issue is never reopened by re-detection, so the answer sticks.
//
// The third answer exists because the first two were the only ones, and
// neither is true of "8560 Cortez Street LLC" — a Private Money borrower.
// Grant: "it is an entity or business, but not a vendor. its a client we
// lend to and serve this business entity." Forcing that record into
// either bucket is a wrong answer, and a card whose every answer is
// wrong is a dead end (Cleanup UX rule #5).
//
// Root-safe (imports nothing) so it's unit-tested from tests/web/.

// Minimal structural subset of CleanupIssueSummary this module reads.
export interface VendorLookalikeIssueLike {
  detectorSlug: string;
}

export function isVendorLookalikeIssue(
  issue: VendorLookalikeIssueLike,
): boolean {
  return issue.detectorSlug === "vendor-lookalike";
}

export const VENDOR_LOOKALIKE_RETAG_LABEL =
  "It's a business that serves us — tag as vendor";

export const VENDOR_LOOKALIKE_CLIENT_LABEL =
  "It's a business we serve — a client, not a vendor";

export const VENDOR_LOOKALIKE_PERSON_LABEL =
  "It's a person — not an issue";

export function vendorLookalikePersonConsequence(): string {
  return "Confirms this really is a person (e.g. a real surname like “Bank”) — the item resolves as not-an-issue and this contact won't be re-flagged. Reversible for 60 seconds, and it can be reopened any time after.";
}

// Grant 2026-08-10 — the client-entity answer. States plainly what it
// does to the record, because "keep-current" alone would read as "do
// nothing" when it in fact re-labels the household.
export function vendorLookalikeClientEntityConsequence(): string {
  return "Records that this is a business Stewardship serves — a borrower or client entity. The contact is NOT tagged as a vendor, so it keeps showing up where a client belongs. If it sits alone in its own household, that household is marked as a business (building icon, “Principals”) and takes the entity's name. The item resolves and won't be re-flagged. Reversible for 60 seconds, and reopenable any time after.";
}
